import { useCallback, useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import ExamGradeGrid from "./ExamGradeGrid";
import { DoctorantCandidacyStatus } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";

const outcomeChip = status => {
  if (status === DoctorantCandidacyStatus.AdmittedToLanguageExam)
    return (
      <Chip
        size="small"
        color="success"
        label="Допуснат до изпит по език"
      />
    );
  if (status === DoctorantCandidacyStatus.Failed)
    return (
      <Chip
        size="small"
        color="error"
        label="Неуспешно"
      />
    );
  if (status === DoctorantCandidacyStatus.Admitted)
    return (
      <Chip
        size="small"
        color="success"
        label="Класиран (приет)"
      />
    );
  if (status === DoctorantCandidacyStatus.NotRanked)
    return (
      <Chip
        size="small"
        color="warning"
        label="Некласиран"
      />
    );
  return (
    <Chip
      size="small"
      label="В процес"
    />
  );
};

const isRanked = status =>
  status === DoctorantCandidacyStatus.Admitted || status === DoctorantCandidacyStatus.NotRanked;

const ExamsSection = () => {
  const notifyError = useNotifyError();
  const candidacy = useRecordContext();
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState(null);

  const candidacyId = candidacy?.id;

  const load = useCallback(async () => {
    if (!candidacyId) return;
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        doctoralRequests.getCandidacyExamGrades(candidacyId),
        null,
        "GET",
      );
      setGrades(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [candidacyId, notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!candidacy) return null;

  const disciplines = grades?.disciplines ?? [];
  const gradesLocked = isRanked(candidacy.status);

  return (
    <SecuredComponent permission={permissions.canEnterDoctoralExamGrades}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>{outcomeChip(candidacy.status)}</Box>
          {gradesLocked && (
            <Typography
              variant="body2"
              sx={{ mb: 2, color: "text.secondary" }}
            >
              Класирането е одобрено — оценките не могат да се променят.
            </Typography>
          )}
          {disciplines.length > 0 ? (
            disciplines.map(block => (
              <Box
                key={block.doctoralDisciplineId}
                sx={{ mb: 4 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Изпит по дисциплина{block.disciplineNameBg ? ` (${block.disciplineNameBg})` : ""}
                </Typography>
                <ExamGradeGrid
                  campaignId={candidacy.doctoralCampaignId}
                  candidacyId={candidacy.id}
                  kind="Discipline"
                  disciplineId={block.doctoralDisciplineId}
                  block={block}
                  locked={gradesLocked}
                  onSaved={load}
                />
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              Кандидатът няма дисциплини за оценяване.
            </Typography>
          )}

          {grades?.language && (
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Изпит по език{candidacy.examLanguageNameBg ? ` (${candidacy.examLanguageNameBg})` : ""}
              </Typography>
              <ExamGradeGrid
                campaignId={candidacy.doctoralCampaignId}
                candidacyId={candidacy.id}
                kind="Language"
                block={grades.language}
                locked={gradesLocked}
                onSaved={load}
              />
            </Box>
          )}
        </>
      )}
    </SecuredComponent>
  );
};

export default ExamsSection;
